/**
 * The destination of a drive that has not ended yet.
 *
 * A head unit is commonly killed with the ignition, so unload handlers are
 * not reliable enough to preserve navigation state. Write synchronously when
 * navigation starts, then clear only on an intentional End or arrival. A
 * restored drive is routed again from the new GPS fix; persisting Mapbox's
 * whole route would resume from stale geometry instead of where the car woke.
 */

import type { Place } from '../core/types';

const KEY = 'wayfarer.active-trip.v1';
const MAX_AGE_MS = 72 * 60 * 60 * 1000;

interface StoredActiveTrip {
  startedAt: number;
  destination: Place;
  stops: Place[];
}

function storedPlace(place: Place): Place {
  return {
    id: place.id,
    name: place.name,
    address: place.address,
    center: [place.center[0], place.center[1]],
    kind: place.kind,
    approximate: place.approximate,
    droppedPin: place.droppedPin,
  };
}

export function rememberActiveTrip(destination: Place, stops: Place[] = []): void {
  const stored: StoredActiveTrip = {
    startedAt: Date.now(),
    destination: storedPlace(destination),
    stops: stops.slice(0, 8).map(storedPlace),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // Navigation must still start when storage is disabled or full.
  }
}

export function loadActiveTrip(): StoredActiveTrip | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<StoredActiveTrip> | null;
    const d = parsed?.destination;
    const lng = d?.center?.[0];
    const lat = d?.center?.[1];
    const age = typeof parsed?.startedAt === 'number' ? Date.now() - parsed.startedAt : Infinity;
    const valid =
      age >= 0 &&
      age <= MAX_AGE_MS &&
      typeof d?.id === 'string' &&
      typeof d.name === 'string' &&
      typeof d.address === 'string' &&
      typeof d.kind === 'string' &&
      typeof lng === 'number' &&
      Number.isFinite(lng) &&
      lng >= -180 &&
      lng <= 180 &&
      typeof lat === 'number' &&
      Number.isFinite(lat) &&
      lat >= -90 &&
      lat <= 90;
    if (!valid) {
      clearActiveTrip();
      return null;
    }
    const storedStops = parsed?.stops;
    const stops = Array.isArray(storedStops)
      ? storedStops.filter(
          (stop): stop is Place =>
            !!stop &&
            typeof stop.id === 'string' &&
            typeof stop.name === 'string' &&
            typeof stop.address === 'string' &&
            typeof stop.kind === 'string' &&
            Array.isArray(stop.center) &&
            Number.isFinite(stop.center[0]) &&
            Number.isFinite(stop.center[1]) &&
            Math.abs(stop.center[0]) <= 180 &&
            Math.abs(stop.center[1]) <= 90,
        ).slice(0, 8)
      : [];
    return { ...(parsed as StoredActiveTrip), stops };
  } catch {
    clearActiveTrip();
    return null;
  }
}

export function clearActiveTrip(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing else can usefully be done when storage is unavailable.
  }
}
